/**
 * useTutorials Composable
 *
 * Manages tutorial state (viewed/not viewed), provides methods
 * to start and reset tutorials. Uses driver.js for rendering.
 *
 * Multi-page support: Steps with `navigateTo` trigger Vue Router
 * navigation. driver.js is re-created per route batch because DOM
 * elements change on navigation.
 *
 * Storage: localStorage with key 'tuscourses-tutorials'
 * Pattern: Singleton state shared across all component instances
 */

import { ref, computed, nextTick } from 'vue'
import { useRouter, type Router } from 'vue-router'
import type { TutorialDefinition, TutorialStep, TutorialWithState } from '@/tutorials/types'
import { tutorials as tutorialRegistry } from '@/tutorials/registry'

// Lazy CSS import — loaded once when first tutorial starts
let cssLoaded = false

const STORAGE_KEY = 'tuscourses-tutorials'

// Singleton state (module-level, shared across all component instances)
const completedIds = ref<Set<string>>(loadCompletedIds())
let routerInstance: Router | null = null

/** Load completed tutorial IDs from localStorage */
function loadCompletedIds(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed)) {
        return new Set(parsed as string[])
      }
    }
  } catch {
    // Corrupted data — start fresh
  }
  return new Set()
}

/** Persist completed IDs to localStorage */
function saveCompletedIds(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...completedIds.value]))
  } catch {
    // localStorage full or unavailable
  }
}

/** Resolve steps: call factory or return static array */
function resolveSteps(def: TutorialDefinition): TutorialStep[] {
  return typeof def.steps === 'function' ? def.steps() : def.steps
}

/** Reactive flag for cross-batch loading state (consumed by App.vue) */
const isTransitioning = ref(false)

/** Wait for a DOM element to appear (with timeout) */
function waitForElement(selector: string, timeout = 5000): Promise<Element | null> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector)
    if (el) return resolve(el)

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector)
      if (found) {
        observer.disconnect()
        resolve(found)
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    setTimeout(() => {
      observer.disconnect()
      resolve(document.querySelector(selector))
    }, timeout)
  })
}

export function useTutorials() {
  // Capture router instance (called from component setup)
  try {
    routerInstance = useRouter()
  } catch {
    // Not in setup context (e.g., unit tests)
  }

  /** All tutorials with their completion state */
  const tutorials = computed<TutorialWithState[]>(() =>
    tutorialRegistry.map((def) => ({
      ...def,
      completed: completedIds.value.has(def.id),
    }))
  )

  /** Check if a specific tutorial is completed */
  function isCompleted(id: string): boolean {
    return completedIds.value.has(id)
  }

  /** Mark tutorial as completed */
  function completeTutorial(id: string): void {
    completedIds.value = new Set([...completedIds.value, id])
    saveCompletedIds()
  }

  /** Reset a tutorial (mark as not completed) */
  function resetTutorial(id: string): void {
    const next = new Set(completedIds.value)
    next.delete(id)
    completedIds.value = next
    saveCompletedIds()
  }

  /** Reset all tutorials */
  function resetAll(): void {
    completedIds.value = new Set()
    saveCompletedIds()
  }

  /** Start a tutorial by ID */
  async function startTutorial(id: string): Promise<void> {
    const def = tutorialRegistry.find((t) => t.id === id)
    if (!def) {
      console.error(`[Tutorials] Unknown tutorial: ${id}`)
      return
    }

    // Lazy-load CSS on first use (driver.js base + TuS theme)
    if (!cssLoaded) {
      await import('driver.js/dist/driver.css')
      await import('@/tutorials/driver-theme.css')
      cssLoaded = true
    }

    const { driver } = await import('driver.js')
    const allSteps = resolveSteps(def)
    const totalSteps = allSteps.length

    /**
     * Run steps starting from a given index.
     * Groups consecutive steps on the same route into a batch,
     * creates a driver.js instance for that batch, and chains
     * to the next batch when done.
     */
    async function runFromStep(fromIndex: number): Promise<void> {
      if (fromIndex >= allSteps.length) {
        completeTutorial(id)
        return
      }

      const currentStep = allSteps[fromIndex]!

      // Navigate if this step has a route
      if (currentStep.navigateTo && routerInstance) {
        isTransitioning.value = true
        await routerInstance.push(currentStep.navigateTo)
        await nextTick()
        // Wait for component mount + initial data loading
        await new Promise((r) => setTimeout(r, 1000))
      }

      // Collect consecutive steps that don't need new navigation
      const batch: TutorialStep[] = [currentStep]
      let nextBatchStart = fromIndex + 1
      while (nextBatchStart < allSteps.length) {
        const nextStep = allSteps[nextBatchStart]!
        if (nextStep.navigateTo) break
        batch.push(nextStep)
        nextBatchStart++
      }

      // Wait for all elements in parallel (much faster than sequential)
      const resolvedElements = new Map<number, boolean>()
      const elementPromises = batch.map((step, i) => {
        if (!step.element) return Promise.resolve()
        return waitForElement(step.element).then((el) => {
          resolvedElements.set(i, el !== null)
          if (!el) {
            console.warn(`[Tutorial] Element not found: "${step.element}"`)
          }
        })
      })
      await Promise.all(elementPromises)

      const isLastBatch = nextBatchStart >= allSteps.length

      return new Promise<void>((resolve) => {
        let localIdx = 0
        let movingToNextBatch = false

        const driverInstance = driver({
          showProgress: false, // We show global progress manually
          animate: true,
          smoothScroll: true,
          overlayColor: 'black',
          overlayOpacity: 0.85,
          stagePadding: 16,
          stageRadius: 8,
          popoverClass: 'tus-tutorial',
          popoverOffset: 12,
          nextBtnText: 'Weiter',
          prevBtnText: 'Zurück',
          // "Fertig" only on the very last step, "Weiter" for all intermediate batch endings
          doneBtnText: isLastBatch ? 'Fertig' : 'Weiter',
          showButtons: ['next', 'previous', 'close'],
          steps: batch.map((step, i) => {
            const globalIdx = fromIndex + i
            const isFirstGlobalStep = globalIdx === 0
            const isFirstInBatchButNotFirst = i === 0 && fromIndex > 0

            return {
              // Only include element if it was actually found in the DOM
              ...(step.element && resolvedElements.get(i) ? { element: step.element } : {}),
              // Per-step stagePadding override
              ...(step.stagePadding != null ? { stagePadding: step.stagePadding } : {}),
              popover: {
                title: step.popover.title,
                description:
                  step.popover.description +
                  `<div class="tus-tutorial-progress">${globalIdx + 1} / ${totalSteps}</div>`,
                ...(step.popover.position ? { side: step.popover.position } : {}),
                // First global step: hide prev button entirely
                ...(isFirstGlobalStep ? { showButtons: ['next', 'close'] } : {}),
                // First step of a later batch: prevent driver.js from disabling prev
                ...(isFirstInBatchButNotFirst ? { disableButtons: [] } : {}),
              },
            }
          }),
          onNextClick: () => {
            localIdx++
            if (localIdx >= batch.length) {
              // Set flag BEFORE destroy to prevent onDestroyStarted from completing
              movingToNextBatch = !isLastBatch
              driverInstance.destroy()
              if (isLastBatch) {
                completeTutorial(id)
                resolve()
              } else {
                isTransitioning.value = true
                runFromStep(nextBatchStart).then(resolve)
              }
            } else {
              driverInstance.moveNext()
            }
          },
          onPrevClick: () => {
            if (localIdx > 0) {
              localIdx--
              driverInstance.movePrevious()
            } else if (fromIndex > 0) {
              // Cross-batch: go back to the previous step
              movingToNextBatch = true
              driverInstance.destroy()
              isTransitioning.value = true
              runFromStep(fromIndex - 1).then(resolve)
            }
          },
          onDestroyStarted: () => {
            // Only complete + resolve if user explicitly closed (not batch transition)
            if (!movingToNextBatch) {
              completeTutorial(id)
            }
            driverInstance.destroy()
            if (!movingToNextBatch) {
              resolve()
            }
          },
        })

        driverInstance.drive()
        isTransitioning.value = false
      })
    }

    await runFromStep(0)
  }

  return {
    tutorials,
    isCompleted,
    isTransitioning,
    startTutorial,
    resetTutorial,
    resetAll,
  }
}
